import { Star, ArrowUpRight } from "@phosphor-icons/react";
import { PageHero } from "@/components/site/PageHero";
import { Reveal } from "@/components/site/Reveal";
import { GALLERY, BUSINESS } from "@/lib/data";
import { useReviews } from "@/lib/useReviews";

const Stars = ({ n = 5 }) => (
  <div className="flex gap-0.5">
    {Array.from({ length: n }).map((_, i) => (
      <Star key={i} size={16} weight="fill" className="text-amber-400" />
    ))}
  </div>
);

export default function Gallery() {
  const { reviews, rating, reviewCount, googleUrl } = useReviews();
  return (
    <div data-testid="gallery-page">
      <PageHero
        testId="gallery-hero"
        eyebrow="Gallery & Reviews"
        lines={["The work.", "The words."]}
        subtitle={`Real projects across ${BUSINESS.area} — and real 5-star reviews from the people who live and work there.`}
      />

      {/* Gallery bento */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-24">
        <Reveal>
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-navy-600">Recent projects</span>
          <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tight text-navy-900 mt-3 leading-[1.1]">
            A look at what we do.
          </h2>
        </Reveal>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12 auto-rows-[200px] md:auto-rows-[240px]">
          {GALLERY.map((g, i) => (
            <Reveal
              key={i}
              delay={(i % 4) * 0.05}
              className={g.span === "lg" ? "col-span-2 row-span-2" : ""}
            >
              <div
                data-testid={`gallery-item-${i}`}
                className="group relative w-full h-full rounded-2xl overflow-hidden border border-slate-200"
              >
                <img
                  src={g.src}
                  alt={g.label}
                  className={`absolute inset-0 w-full h-full ${g.fit === "contain" ? "object-contain bg-slate-100" : "object-cover"} transition-transform duration-700 group-hover:scale-110`}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-900/85 via-transparent to-transparent opacity-80 group-hover:opacity-100 transition-opacity" />
                <span className="absolute bottom-4 left-4 right-4 font-display font-bold text-white text-lg tracking-tight translate-y-2 group-hover:translate-y-0 transition-transform">
                  {g.label}
                </span>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Reviews */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-24">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-navy-600">Google Reviews</span>
              <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tight text-navy-900 mt-3 leading-[1.1]">
                Rated {rating} by our clients.
              </h2>
            </div>
            <div className="flex items-center gap-4 bg-white border border-slate-200 rounded-2xl px-6 py-4">
              <span className="font-display font-black text-5xl text-navy-900">{rating}</span>
              <div>
                <Stars />
                <span className="text-sm text-slate-500 mt-1 block">{reviewCount}+ reviews</span>
              </div>
            </div>
          </div>

          <div className="columns-1 md:columns-2 lg:columns-3 gap-5 space-y-5">
            {reviews.map((r, i) => (
              <Reveal key={i} delay={(i % 3) * 0.06}>
                <div
                  data-testid={`review-${i}`}
                  className="break-inside-avoid bg-white rounded-2xl border border-slate-200 p-7 hover:shadow-[0_12px_40px_rgb(10,17,40,0.08)] transition-shadow"
                >
                  <div className="flex items-center justify-between">
                    <Stars n={r.stars || 5} />
                    <span className="text-xs text-slate-400">{r.date}</span>
                  </div>
                  <p className="text-navy-900 mt-4 leading-relaxed font-medium">“{r.text}”</p>
                  <div className="mt-5 pt-4 border-t border-slate-100 flex items-center gap-3">
                    {r.photo ? (
                      <img src={r.photo} alt={r.name} className="w-9 h-9 rounded-full object-cover" />
                    ) : (
                      <span className="w-9 h-9 rounded-full bg-navy-900 text-white grid place-items-center font-bold text-sm">
                        {r.name.charAt(0)}
                      </span>
                    )}
                    <span className="font-bold text-navy-900">{r.name}</span>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="text-center mt-12">
            <a
              href={googleUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="view-google-reviews"
              className="inline-flex items-center gap-2 rounded-full bg-navy-900 text-white px-8 py-4 font-bold hover:bg-navy-600 transition-colors"
            >
              Read all reviews on Google <ArrowUpRight weight="bold" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
